import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Modal } from '@/components/custom/modal/modal';
import QuestionCard from './QuestionCard';
import { useClient } from 'jsonapi-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from '@/components/custom/loader';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { EmptyState } from '@/components/custom/empty-state';
import { useSession } from '@/hooks/useSession';
import EndScroll from '@/components/custom/end-scroll/end-scroll';
import GateFilters from './GateFilters';
import LegacyFilters from './LegacyFilters';

export default function AddQuestionModal(props) {
  const location = useLocation();
  const pageUrl = location.pathname;
  const currentUser = useSession().session?.user;
  const { t } = useTranslation();
  const { show, setShowModal, setSelectedQuestions, selectedQuestions, selectedQuestionBook, addQuestion, removeQuestion, filteredQuestions, selectedTaxonomy, selectedExam } = props;
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const client = useClient();
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceTimer = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  function getExamType(url) {
    const parts = url.split('/');
    const value = parts[1];
    return value;
  }

  const examType = getExamType(pageUrl);
  // Filtros GATE (Etapa, server-side) só em simulados;
  // provas e apostilas usam os filtros legados (modalidade/busca/inéditas, client-side).
  const isMock = examType === 'simulados';

  const resetResults = () => {
    setQuestions([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const resetPagination = () => {
    setQuestions([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const getQuestions = async () => {
    setLoading(true);
    let url;
    switch (examType) {
      case 'simulados':
        url = `questions?filter[visibility]=[mock, exam_and_mock, train, open]&filter[taxonomies.id]=${selectedTaxonomy?.taxonomy_id}&filter[target_visibility]=open&page[size]=30&simple=true&page[number]=${pageNumber}`;
        break;
      case 'apostilas':
        url = `questions?filter[visibility]=[train, mock]&filter[taxonomies.id]=${selectedTaxonomy?.taxonomy_id}&filter[target_visibility]=train&page[size]=30&simple=true&page[number]=${pageNumber}`;
        break;
      case 'provas':
        url = `questions?filter[visibility]=[exam, exam_and_mock, train, open]&filter[taxonomies.id]=${selectedTaxonomy?.taxonomy_id}&filter[target_visibility]=exam&page[size]=30&simple=true&page[number]=${pageNumber}`;
        break;
    }

    if (isMock) {
      (filters.values.etapa || []).forEach((e, i) => {
        url += `&filter[etapa][${i}]=${e}`;
      });
    } else {
      if (filters.values.modality !== 'blank') {
        url += `&filter[modality]=${filters.values.modality}`;
      }
      if (filters.values.searchTerm !== '') {
        url += `&q=${filters.values.searchTerm}`;
      }
      if (filters.values.unused) {
        url += `&unused_only=true`;
      }
    }

    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error(t('toast.errorGetQuestions'));
      setHasMore(false);
    } else {
      if (data.length < 30) {
        setHasMore(false);
      }

      setQuestions(prev => [
        ...prev,
        ...data.filter(item => {
          if (item.published !== true) return false;

          if (item['authored-by-user']) {
            return item?.creator?.id === currentUser?.id;
          }

          return true;
        })
      ]);
    }
    setLoading(false);
  };

  const filters = useFormik({
    initialValues: {
      modality: 'blank',
      searchTerm: '',
      unused: false,
      etapa: []
    }
  });

  const modalitys = [
    { name: t('exams.selectTrueOrFalse'), id: 2 },
    {
      name: t('exams.selectMultipleChoice'),
      id: 1
    },
    ...(examType === 'provas' ? [{ name: t('exams.selectDiscursiveQuestion'), id: 3 }] : [])
  ];

  const onCloseModal = () => {
    setShowModal(false);
    resetPagination();
  };

  useEffect(() => {
    if (!show) return;

    setQuestions([]);
    setHasMore(true);
    setPageNumber(1);
    setRefreshKey(prev => prev + 1);
  }, [filters.values.modality, filters.values.unused, filters.values.etapa, debouncedSearchTerm, selectedTaxonomy?.taxonomy_id, show]);

  useEffect(() => {
    if (!show || !hasMore) return;

    getQuestions();
  }, [pageNumber, refreshKey]);

  useEffect(() => {
    if (show) {
      filters.setFieldValue('searchTerm', '');
      filters.setFieldValue('modality', 'blank');
      filters.setFieldValue('unused', false);
      filters.setFieldValue('etapa', []);
    }
  }, [show]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(filters.values.searchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer.current);
  }, [filters.values.searchTerm]);

  return (
    <Modal
      show={show}
      onClose={onCloseModal}
      contentClassName="modal__content--lg"
    >
      <h3 className="modal__simple-title">
        {t('exams.addQuestionTitle')} {selectedTaxonomy?.name}
      </h3>

      {isMock ? (
        <GateFilters
          filters={filters}
          modalitys={modalitys}
          resetResults={resetResults}
          t={t}
        />
      ) : (
        <LegacyFilters
          filters={filters}
          modalitys={modalitys}
          t={t}
        />
      )}

      <div
        className="round-dg-wrapper"
        style={{ height: '55vh' }}
        id="modal"
      >
        {loading && questions?.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Loader />
          </div>
        ) : questions?.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <EmptyState type="content" />
          </div>
        ) : (
          <InfiniteScroll
            scrollableTarget="modal"
            dataLength={questions?.length}
            next={() => setPageNumber(prevState => prevState + 1)}
            hasMore={hasMore}
            loader={<Loader />}
            endMessage={<EndScroll />}
          >
            <table className="round-dg round-dg--light">
              <tbody className="round-dg__body">
                {questions?.map((question, index) => {
                  const selectedQuestion = filteredQuestions.find(item => item.id === question.id);
                  return (
                    <QuestionCard
                      removeQuestion={removeQuestion}
                      addQuestion={addQuestion}
                      selectedQuestionBook={selectedQuestionBook}
                      selectedQuestions={selectedQuestions}
                      setSelectedQuestions={setSelectedQuestions}
                      question={question}
                      isSelected={!!selectedQuestion}
                      addBtn
                      hideActions
                      id={index}
                      key={index}
                    />
                  );
                })}
              </tbody>
            </table>
          </InfiniteScroll>
        )}
      </div>
    </Modal>
  );
}
